import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { searchApi, SearchAnalyticsResponse } from '../api/search'
import SearchAnalytics from '../components/SearchAnalytics'

export default function AnalyticsPage() {
  const [searchParams] = useSearchParams()
  const searchId = searchParams.get('searchId')
  const filteredParam = searchParams.get('filtered')
  const isFiltered = filteredParam === 'true'

  const [data, setData] = useState<SearchAnalyticsResponse | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [excludedKeywords, setExcludedKeywords] = useState<string[]>([])

  useEffect(() => {
    if (!searchId) {
      // Try to get last search from sessionStorage
      const lastId = sessionStorage.getItem('pmc_ev_last_search_id')
      const lastFiltered = sessionStorage.getItem('pmc_ev_last_search_filtered')
      if (lastId) {
        window.location.href = `/analytics?searchId=${lastId}&filtered=${lastFiltered === 'true'}`
      }
      return
    }

    setIsLoading(true)
    setError('')
    searchApi.getAnalytics(searchId, isFiltered, { exclude_keywords: excludedKeywords })
      .then(res => setData(res.data))
      .catch((e: any) => {
        setError(e.response?.data?.detail || 'Failed to load analytics')
      })
      .finally(() => setIsLoading(false))
  }, [searchId, isFiltered, excludedKeywords])

  const handleExcludeChange = (newExcluded: string[]) => {
    setExcludedKeywords(newExcluded)
  }

  if (!searchId) {
    return (
      <div className="text-center py-16">
        <h2 className="text-xl font-semibold text-gray-700 mb-2">No Search Selected</h2>
        <p className="text-gray-500 mb-4">Run a search first to see analytics.</p>
        <a href="/" className="text-blue-600 hover:underline">Go to Search</a>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="text-center py-16 text-gray-400">
        <div className="text-lg mb-2">Loading analytics...</div>
        <div className="text-sm">This may take a moment for large result sets.</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-16 text-red-500">
        <div className="text-lg mb-2">Error loading analytics</div>
        <div className="text-sm">{error}</div>
      </div>
    )
  }

  if (!data) {
    return (
      <div className="text-center py-16 text-gray-400">
        No analytics data available.
      </div>
    )
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Search Analytics</h1>
        <p className="text-gray-500 text-sm mt-1">
          {data.total_articles.toLocaleString()} unique articles
          {isFiltered && <span className="text-blue-600 font-medium ml-2">(filtered)</span>}
        </p>
      </div>
      <SearchAnalytics
        data={data}
        excludedKeywords={excludedKeywords}
        onExcludeChange={handleExcludeChange}
      />
    </div>
  )
}
